import {
  Breadcrumb, Button, Card, Col, Form, ListGroup, Row
} from "react-bootstrap";
import { useStateMachine } from "little-state-machine";
import { Divider, Spinner, Stepper } from "@whitespace/whitespace-ui";
import { FlowOrigination } from "interfaces/FlowOrigination";
import { useEffect, useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import IRegionHwrc from "interfaces/models/IRegionHwrc";
import { Link, useNavigate } from "react-router-dom";
import { assignmentStepsHWRCRoleAdminLogisticsTeam, assignmentStepsHWRCRoleSite } from "modules/assignmentSteps";
import { useAmplifyREST } from "hooks/useAmplifyREST";
import { mapClaimToRegion } from "mappers/mapClaimToRegion/mapClaimToRegion";
import AmplifyError from "components/AmplifyError/AmplifyError";
import IAssignHwrcPermissionsRequest from "interfaces/requests/IAssignHwrcPermissionsRequest";
import { updateAssignHwrcPermissionsRequest } from "actions/updateAssignHwrcPermissionsRequest";
import { initialAssignHwrcPermissionsRequest } from "modules/initialAssignHwrcPermissionsRequest";
import IClaimHwrc from "interfaces/responses/IClaimHwrc";
import { HwrcRoles } from "interfaces/HwrcRoles";
import { ClaimType } from "interfaces/models/ClaimType";
import UserRegionItem from "./UserRegionItem/UserRegionItem";

interface AssignUserRegionsProps {
  flowOrigination : FlowOrigination
}

const AssignUserRegions = ({ flowOrigination } : AssignUserRegionsProps) => {
  const navigate = useNavigate();
  const { actions } = useStateMachine({
    updateAssignHwrcPermissionsRequest
  });
  const { state } = useStateMachine();

  const {
    isSending: getRegionsIsSending, errors: getRegionsErrors, data: getRegionsData, sendRequest: getRegionsSendRequest
  } = useAmplifyREST();

  const {
    isSending: addClaimsIsSending, errors: addClaimsErrors, statusCode: addClaimsStatusCode, sendRequest: addClaimsSendRequest
  } = useAmplifyREST();

  const {
    isSending: removeClaimsIsSending, errors: removeClaimsErrors, statusCode: removeClaimsStatusCode, sendRequest: removeClaimsSendRequest
  } = useAmplifyREST();

  const {
    isSending: allocateIsSending, errors: allocateErrors, statusCode: allocateStatusCode, sendRequest: allocateSendRequest, resetState: allocateResetState
  } = useAmplifyREST();

  const [regions, setRegions] = useState<IRegionHwrc[] | undefined>(undefined);
  const [assignmentSteps, setAssignmentSteps] = useState<string[]>([]);
  const [ignoreClaimRemoval, setIgnoreClaimRemoval] = useState(false);
  const [selectButtonDisabled, setSelectButtonDisabled] = useState(false);
  const [saveButtonDisabled, setSaveButtonDisabled] = useState(false);
  const isSending = getRegionsIsSending || addClaimsIsSending || removeClaimsIsSending || allocateIsSending;
  const errors = getRegionsErrors || addClaimsErrors || removeClaimsErrors || allocateErrors;

  const {
    register, handleSubmit, getValues, setValue, watch
  } = useForm({
    mode: "onSubmit"
  });

  useEffect(() => {
    switch (state.assignHwrcPermissionsRequest!.roleId) {
      case HwrcRoles.Admin:
      case HwrcRoles.LogisticsTeam:
      {
        setAssignmentSteps(assignmentStepsHWRCRoleAdminLogisticsTeam);
        break;
      }
      case HwrcRoles.Site:
      default:
      {
        setAssignmentSteps(assignmentStepsHWRCRoleSite);
        break;
      }
    }
  }, []);

  useEffect(() => {
    // eslint-disable-next-line max-len
    const url = `apps/${encodeURIComponent(state.selectedUserApplicationDetails!.applicationDetails.applicationId)}/claim_types/regions/claims`;
    getRegionsSendRequest(url, "GET", null);
  }, [getRegionsSendRequest]);

  useEffect(() => {
    if (getRegionsData) {
      setRegions(
        getRegionsData.map(
          (claim : IClaimHwrc) => mapClaimToRegion(claim)
        )
      );
    }
  }, [getRegionsData]);

  useEffect(() => {
    if ((removeClaimsStatusCode === 204 || ignoreClaimRemoval) && addClaimsStatusCode === 204) {
      if (flowOrigination === FlowOrigination.UserManagement) {
        navigate("/manageuserapplications");
      } else {
        navigate("/requests");
      }
    }
  }, [removeClaimsStatusCode, addClaimsStatusCode]);

  const watchFields = watch(["regions"]);

  const renderRegions = () => regions?.map((region : IRegionHwrc, idx : number) => (
    <UserRegionItem register={register} key={`userRegionItem_${idx}`} region={region} />
  ));

  const constructRequestFromForm = useCallback(() => {
    const request : IAssignHwrcPermissionsRequest = initialAssignHwrcPermissionsRequest();
    request.applicationId = state.assignHwrcPermissionsRequest!.applicationId;
    request.userId = state.assignHwrcPermissionsRequest!.userId;
    request.roleId = state.assignHwrcPermissionsRequest!.roleId;
    request.regionIds = [];
    request.siteIds = state.assignHwrcPermissionsRequest!.siteIds;
    const regionsSelected = getValues("regions") as string[];

    if (regionsSelected) {
      if (typeof regionsSelected === "string") {
        request!.regionIds.push(regionsSelected);
      } else {
        regionsSelected.forEach((siteId : string) => {
          request!.regionIds.push(siteId);
        });
      }
    }
    actions.updateAssignHwrcPermissionsRequest(request);
  }, [getValues, state.assignHwrcPermissionsRequest]);

  const setControlValues = useCallback(() => {
    if (getRegionsData && state.assignHwrcPermissionsRequest && state.assignHwrcPermissionsRequest?.regionIds.length > 0) {
      if (!(getRegionsData && getRegionsData.length === 1
        && !state.assignHwrcPermissionsRequest!.regionIds.includes(getRegionsData[0].id))) {
        setValue("regions", state.assignHwrcPermissionsRequest.regionIds.map(String));
      }
    }
  }, [setValue, getRegionsData, state.assignHwrcPermissionsRequest]);

  useEffect(() => {
    const regionsSelected = getValues("regions") as string[];
    const areRegionsSelected = regionsSelected && regionsSelected.length > 0;
    setSelectButtonDisabled(!areRegionsSelected);
    setSaveButtonDisabled(!areRegionsSelected);
  }, [watchFields]);

  useEffect(() => {
    setControlValues();
  }, [setControlValues]);

  const applyRegions = () => {
    // eslint-disable-next-line max-len
    const url = `apps/${encodeURIComponent(state.selectedUserApplicationDetails!.applicationDetails.applicationId)}/users/${encodeURIComponent(state.selectedUser!.userId)}/claims`;
    const regionsSelected = getValues("regions") as string[];

    const claims : IClaimHwrc[] = [];

    const roleClaim : IClaimHwrc = {
      id: state.assignHwrcPermissionsRequest!.roleId!,
      displayName: "",
      type: ClaimType.Role
    };

    claims.push(roleClaim);

    if (typeof regionsSelected === "string") {
      claims.push((getRegionsData.find((regionClaim : IClaimHwrc) => regionClaim.id === regionsSelected)));
    } else {
      regionsSelected.forEach((regionId : string) => {
        claims.push((getRegionsData.find((regionClaim : IClaimHwrc) => regionClaim.id === regionId)));
      });
    }

    addClaimsSendRequest(url, "PUT", claims);
  };

  const toggleAllocated = () => {
    const method = "PUT";
    // eslint-disable-next-line max-len
    const url = `users/${encodeURIComponent(state.selectedUser!.userId)}/apps/${encodeURIComponent(state.selectedUserApplicationDetails!.applicationDetails.applicationId)}`;
    allocateSendRequest(url, method);
  };

  const removeClaims = () => {
    const claimsToRemove = state.assignedClaimsHwrc!;

    if (claimsToRemove.length > 0) {
    // eslint-disable-next-line max-len
      const url = `apps/${encodeURIComponent(state.selectedUserApplicationDetails!.applicationDetails.applicationId)}/users/${encodeURIComponent(state.selectedUser!.userId)}/claims`;
      removeClaimsSendRequest(url, "DELETE", claimsToRemove);
    } else {
      setIgnoreClaimRemoval(true);
    }
  };

  useEffect(() => {
    if ((removeClaimsStatusCode === 204)) {
      applyRegions();
    }
  }, [removeClaimsStatusCode]);

  const updateClaims = () => {
    removeClaims();
    applyRegions();
  };

  const handleSaveClick = () => {
    constructRequestFromForm();
    updateClaims();
    toggleAllocated();
  };

  const handleSelectClick = () => {
    constructRequestFromForm();
    if (flowOrigination === FlowOrigination.UserManagement) {
      navigate("/assignuserhwrcsites");
    } else {
      navigate("/requestsassignuserhwrcsites");
    }
  };

  const handleBackButtonClick = () => {
    constructRequestFromForm();
    if (flowOrigination === FlowOrigination.UserManagement) {
      navigate("/assignuserhwrcroles");
    } else {
      navigate("/requestsassignuserhwrcroles");
    }
  };

  return (
    <>
      {errors && <AmplifyError errors={errors} />}
      <Card>
        <Card.Header>
          <Row className="w-100">
            <Col xs={8}>
              <h1>
                Assign Regions
                <Spinner animation="grow" visible={isSending} spinnerText="Loading" variant="info" />
              </h1>
            </Col>
            <Col xs={4} className="d-flex justify-content-end">
              <span data-testid="selected-user-title" className="selected-user-title">{state.selectedUser?.username}</span>
            </Col>
          </Row>
        </Card.Header>
        <Card.Body className="no-padding">
          <Row className="d-flex w-100 align-items-center top-margin-small bottom-margin-small">
            <Col xs={8} className="d-flex">
              <Breadcrumb className="breadcrumb-manage-user left-margin-large no-wrap">
                {flowOrigination === FlowOrigination.Requests
            && (
              <>
                <Breadcrumb.Item
                  data-testid="breadcrumb-requests"
                  linkAs={Link}
                  linkProps={{ to: "/requests" }}
                  onClick={() => navigate("/requests")}
                >
                  Requests
                </Breadcrumb.Item>
                <Breadcrumb.Item data-testid="breadcrumb-site-url" active>
                  {state.selectedUserApplicationDetails!.applicationDetails.siteUrl}
                </Breadcrumb.Item>
                <Breadcrumb.Item
                  data-testid="breadcrumb-roles"
                  linkAs={Link}
                  linkProps={{ to: "/requestsassignuserhwrcroles" }}
                  onClick={() => navigate("/requestsassignuserhwrcroles")}
                >
                  Roles
                </Breadcrumb.Item>
                <Breadcrumb.Item data-testid="breadcrumb-regions" active>
                  Regions
                </Breadcrumb.Item>

              </>
            )}
                {flowOrigination === FlowOrigination.UserManagement
            && (
              <>
                <Breadcrumb.Item
                  data-testid="breadcrumb-users"
                  linkAs={Link}
                  linkProps={{ to: "/users" }}
                  onClick={() => navigate("/users")}
                >
                  Users
                </Breadcrumb.Item>

                <Breadcrumb.Item
                  data-testid="breadcrumb-manage-user"
                  linkAs={Link}
                  linkProps={{ to: "/manageuser" }}
                  onClick={() => navigate("/manageuser")}
                >
                  Manage User
                </Breadcrumb.Item>
                <Breadcrumb.Item
                  data-testid="breadcrumb-manage-user-applications"
                  linkAs={Link}
                  linkProps={{ to: "/manageuserapplications" }}
                  onClick={() => navigate("/manageuserapplications")}
                >
                  Applications
                </Breadcrumb.Item>
                <Breadcrumb.Item data-testid="breadcrumb-site-url" active>
                  {state.selectedUserApplicationDetails!.applicationDetails.siteUrl}
                </Breadcrumb.Item>
                <Breadcrumb.Item
                  data-testid="breadcrumb-roles"
                  linkAs={Link}
                  linkProps={{ to: "/assignuserhwrcroles" }}
                  onClick={() => navigate("/assignuserhwrcroles")}

                >
                  Roles
                </Breadcrumb.Item>

                <Breadcrumb.Item data-testid="breadcrumb-regions" active>
                  Regions
                </Breadcrumb.Item>

              </>
            )}
              </Breadcrumb>
            </Col>
          </Row>
          <Divider />
          <Form noValidate onSubmit={handleSubmit(handleSelectClick)}>
            <Row className="d-flex w-100 align-items-center top-margin-small bottom-margin-small">
              <Col xs={8} className="d-flex">
                <span className="w-100">
                  <span data-testid="stepper-container" className="w-100">
                    <Stepper className="w-100 ms-4 mb-0" steps={assignmentSteps} currentStep={1} />
                  </span>
                </span>
              </Col>
              <Col xs={4} className="d-flex justify-content-end">
                <Button variant="secondary" className="me-2" onClick={() => handleBackButtonClick()}>
                  Back
                </Button>
                {state.assignHwrcPermissionsRequest?.roleId === HwrcRoles.Site
                && (
                  <Button data-testid="btnSelect" variant="primary" type="submit" disabled={selectButtonDisabled || isSending}>
                    Select
                  </Button>
                ) }

                {state.assignHwrcPermissionsRequest?.roleId !== HwrcRoles.Site
                && (
                  <Button data-testid="btnSave" variant="primary" type="button" disabled={saveButtonDisabled || isSending} onClick={() => handleSaveClick()}>
                    Save
                  </Button>
                ) }

              </Col>
            </Row>
            <Divider />
            <ListGroup variant="flush" className="striped highlight">
              {renderRegions()}
            </ListGroup>
          </Form>

        </Card.Body>
      </Card>
    </>
  );
};

export default AssignUserRegions;
